// ------------------------------------------------------------------
// ReportDisclaimer — a report's legal/liability footnote, set apart from
// the report's own findings (story 4-81; P06 §4).
//
// `ReportContent.disclaimer` (story 4-80) is a real wire field but null
// today by design: no persisted source of disclaimer copy exists anywhere
// in the product yet — resolving real per-org/per-template copy is a
// customization dimension for story 4-82 to add, not a guess this story
// invents. So this primitive takes plain `text` and renders nothing when a
// caller has none; a caller decides whether to render it at all (both
// renderers gate it on the `disclaimer` Content-tab toggle AND a non-null
// value — see `ReportRender.tsx`).
//
// a11y: `role="note"` — an annotation about the document, not the primary
// content, which is exactly what a screen reader user benefits from being
// told before the caveat text starts.
export interface ReportDisclaimerProps {
  text: string;
  headingLevel?: 2 | 3 | 4;
  className?: string;
  /** Story 4-117 — additive, default `'Disclaimer'` (byte-identical to every
   *  pre-4-117 caller). This story reuses this SAME primitive for the new
   *  `methodology` static block (`title="Methodology"`) rather than a
   *  second, near-identical component — see `ReportContentToggles
   *  .methodology`'s own comment for why "introduction" gets no sibling
   *  block here. */
  title?: string;
}

export function ReportDisclaimer({ text, headingLevel = 3, className = '', title = 'Disclaimer' }: ReportDisclaimerProps): JSX.Element {
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  return (
    <section role="note" aria-label={title} className={`space-y-1 border-t border-border/60 pt-3 ${className}`}>
      <Heading className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{title}</Heading>
      <p className="whitespace-pre-line text-xs leading-relaxed text-muted-foreground">{text}</p>
    </section>
  );
}
